import React, { useState, useEffect } from 'react';
import { 
  Send, X, Copy, CheckCircle, User, Loader2, ArrowLeft, ChevronRight, Search, Play, Image as ImageIcon, ShoppingBag 
} from 'lucide-react';
import { collection, query, where, getDocs, doc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';

const fetchUserList = async (userIds: string[]) => {
  if (!userIds || userIds.length === 0) return [];
  try {
    const idsToFetch = userIds.slice(0, 15);
    const usersData = await Promise.all(idsToFetch.map(async (uid) => {
      const snap = await getDoc(doc(db, 'users', uid));
      return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
    }));
    return usersData.filter(u => u !== null);
  } catch (error) { return []; }
};

export const ShareModal = ({ isOpen, onClose, shareData, userProfile, requireAuth }: any) => {
  const { user } = useAuth();
  const [view, setView] = useState<'menu' | 'users'>('menu');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [suggested, setSuggested] = useState<any[]>([]);
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (userProfile?.followingUsers && userProfile.followingUsers.length > 0) {
      fetchUserList(userProfile.followingUsers).then(setSuggested);
    }
  }, [userProfile]);

  useEffect(() => {
    if (view === 'users') {
      if (search.length > 0) {
        const q = query(
          collection(db, 'users'), 
          where('displayName', '>=', search),
          where('displayName', '<=', search + '\uf8ff')
        );
        getDocs(q).then(snap => {
          let res = snap.docs.map(d => ({ uid: d.id, ...d.data() as any }));
          res = res.filter(u => {
            const isBrand = u.isBrand === true || 
                            String(u.isBrand).toLowerCase() === 'true' || 
                            String(u.role).toLowerCase() === 'brand' || 
                            String(u.accountType).toLowerCase() === 'brand' || 
                            String(u.accountType).toLowerCase() === 'business' ||
                            u.businessName; 
            return !isBrand;
          });
          setResults(res);
        });
      } else { setResults([]); }
    }
  }, [view, search]);

  const handleCopy = () => {
    let link = window.location.origin; 
    if (shareData?.type === 'post') {
      const post = shareData.payload;
      // Support both normal posts and thrift items
      if (post.type === 'thrift') {
        link += `/thrifts?post=${post.id}`;
      } else {
        link += `/post/${post.id}`;
      }
    } else {
      link += window.location.pathname;
    }
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendToUser = (targetUser: any) => {
    requireAuth(async () => {
      if (!user || !shareData) return;
      setSendingTo(targetUser.uid);
      try {
        const chatId = [user.uid, targetUser.uid].sort().join('_');
        const chatRef = doc(db, 'chats', chatId);
        let sharePayload: any = {
          text: "",
          senderId: user.uid, 
          createdAt: serverTimestamp(), 
          read: false
        };
        let previewText = "Shared an asset";
        if (shareData.type === 'post') {
          const post = shareData.payload;
          sharePayload.messageType = 'post_share';
          sharePayload.post = { 
            type: post.type || 'post', 
            id: post.id, 
            authorName: post.authorName || post.userName,
            authorId: post.authorId || post.userId,
            authorImage: post.authorImage || post.userAvatar,
            title: post.itemName || post.title || post.caption || 'Asset',
            image: post.image || (post.images && post.images[0]) || post.imageUrl,
            videoUrl: post.videoUrl || post.video || null
          };
        }
        await setDoc(chatRef, {
          participants: [user.uid, targetUser.uid],
          participantDetails: { 
            [user.uid]: { displayName: userProfile?.displayName || 'User', photoURL: userProfile?.photoURL || userProfile?.photoUrl || userProfile?.avatar || '' },
            [targetUser.uid]: { displayName: targetUser.displayName, photoURL: targetUser.photoURL || targetUser.photoUrl || targetUser.avatar || '' }
          },
          lastMessage: { text: previewText, createdAt: serverTimestamp(), unread: true, senderId: user.uid },
          updatedAt: serverTimestamp()
        }, { merge: true });
        await addDoc(collection(db, 'chats', chatId, 'messages'), sharePayload);
        setSendingTo(null); onClose(); 
      } catch (e) { console.error("Failed to send", e); setSendingTo(null); }
    }, "Log in to share this with a friend.");
  };

  if (!isOpen || !shareData) return null;
  const displayList = search.length > 0 ? results : suggested;
  const listTitle = search.length > 0 ? "Search Results" : "Suggested Contacts";

  const renderPreview = () => {
    if (shareData.type === 'post') {
      const post = shareData.payload;
      const title = post.itemName || post.title || post.caption || 'Asset';
      const image = post.image || (post.images && post.images[0]) || post.imageUrl;
      const author = post.userName || post.authorName || 'User';
      const hasVideo = post.videoUrl;
      return (
        <div className="flex gap-4 items-center">
          <div className="w-16 h-16 bg-zinc-900 rounded-xl lg:rounded-none overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center relative">
            {hasVideo ? (
              <>
                <video src={post.videoUrl} className="w-full h-full object-cover grayscale" muted playsInline />
                <Play size={16} className="absolute text-white/70" />
              </>
            ) : image ? (
              <img src={image} className="w-full h-full object-cover grayscale" alt="Preview" />
            ) : (
              <ShoppingBag size={24} className="text-zinc-600" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-white text-sm truncate uppercase">{title}</p>
            <p className="text-zinc-500 text-[10px] font-bold uppercase truncate mt-1">By @{author}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
      <div className="w-full max-w-sm lg:max-w-md bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none flex flex-col max-h-[80vh] shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-zinc-900 flex justify-between items-center">
          {view === 'users' ? <button onClick={() => setView('menu')} className="p-2 bg-black rounded-full lg:rounded-none text-white hover:bg-zinc-800 transition-colors"><ArrowLeft size={16}/></button> : <h3 className="text-sm font-black text-white uppercase tracking-widest ml-2">Share</h3>}
          <button onClick={onClose} className="p-2 bg-black rounded-full lg:rounded-none text-zinc-500 hover:text-white transition-colors"><X size={18}/></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto scrollbar-hide">
          {view === 'menu' && (
            <div className="space-y-4">
              <div className="p-4 bg-black rounded-2xl lg:rounded-none border border-zinc-900 mb-6">
                {renderPreview()}
              </div>
              <button onClick={handleCopy} className="w-full p-4 rounded-2xl lg:rounded-none text-black font-bold text-sm flex items-center justify-center gap-3 transition-all active:scale-95 bg-white hover:bg-zinc-200 uppercase tracking-widest">
                {copied ? <CheckCircle size={18} className="text-black"/> : <Copy size={18}/>} {copied ? "Link Copied" : "Copy Link"}
              </button>
              <button onClick={() => requireAuth(() => setView('users'), "Log in to share internally via DM.")} className="w-full p-4 rounded-2xl lg:rounded-none bg-black text-white border border-zinc-800 hover:border-white font-bold text-sm flex items-center justify-between transition-all active:scale-95 uppercase tracking-widest">
                <div className="flex items-center gap-3"><Send size={18}/> Send in DM</div>
                <ChevronRight size={16} className="text-zinc-500"/>
              </button>
            </div>
          )}
          {view === 'users' && (
            <div className="space-y-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
                <input autoFocus className="w-full bg-black rounded-full lg:rounded-none border border-zinc-800 py-3 pl-12 pr-4 text-sm text-white outline-none focus:border-white placeholder:text-zinc-600 transition-all font-mono" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)}/>
              </div>
              <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-2">{listTitle}</h4>
              <div className="space-y-2">
                {displayList.map(u => (
                  <button key={u.uid} disabled={sendingTo === u.uid} onClick={() => handleSendToUser(u)} className="w-full flex items-center justify-between p-3 bg-black rounded-2xl lg:rounded-none border border-zinc-900 hover:border-white transition-all disabled:opacity-50 group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full lg:rounded-none bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center text-white">
                        {u.photoURL || u.photoUrl || u.avatar ? <img src={u.photoURL || u.photoUrl || u.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt=""/> : <User size={16}/>}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-black uppercase tracking-widest text-xs">{u.displayName}</p>
                        <p className="text-zinc-500 font-mono text-[9px] mt-0.5 uppercase">@{u.username || 'user'}</p>
                      </div>
                    </div>
                    {sendingTo === u.uid ? <Loader2 size={16} className="animate-spin text-white"/> : <div className="bg-white text-black text-[10px] font-bold px-4 py-1.5 rounded-full lg:rounded-none opacity-0 group-hover:opacity-100 transition-opacity uppercase">Send</div>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};